import { useEffect, useState } from "react";
import { Building2, Plus, Pencil, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge2 } from "@/components/common";
import AgentDiscountNote from "@/components/AgentDiscountNote";

const EMPTY = { name: "", contact_name: "", contact_email: "", contact_phone: "", city: "" };
const FIELDS = [["name", "Developer company name *"], ["contact_name", "Client contact person"], ["contact_email", "Client email"], ["contact_phone", "Client phone"], ["city", "City"]];

function CompanyDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(value); }, [value]);
  const save = async () => {
    setBusy(true);
    try {
      await (f.id ? api.put(`/agent/companies/${f.id}`, f) : api.post("/agent/companies", f));
      toast.success(f.id ? "Company updated" : "Company onboarded");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!value} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-800" data-testid="agent-company-dialog">
        <DialogHeader><DialogTitle>{f?.id ? "Edit developer company" : "Onboard a developer company"}</DialogTitle></DialogHeader>
        {f && <div className="space-y-3">{FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`agent-company-${k}-input`} /></div>))}</div>}
        <DialogFooter><Button onClick={save} disabled={busy || !f?.name?.trim()} data-testid="agent-company-save">{busy ? "Saving..." : "Save company"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyCard({ c, current, onOpen, onEdit }) {
  return (
    <div className={`lx-card flex flex-col gap-3 ${current ? "ring-2 ring-emerald-500/50" : ""}`} data-testid={`agent-company-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{c.name}</div>
          <div className="text-xs text-slate-500 truncate">{[c.contact_name, c.city].filter(Boolean).join(" · ") || (c.is_agency ? "Your own agency workspace" : "No contact added")}</div>
        </div>
        <div className="flex gap-1 shrink-0">{c.is_agency && <Badge2 tone="cyan">My agency</Badge2>}{c.status !== "active" && <Badge2 tone="rose">{c.status}</Badge2>}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        {[["Projects", c.projects], ["Leads", c.leads], ["Campaigns", c.campaigns]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-800 py-2"><div className="font-mono text-base font-bold">{v}</div><div className="text-slate-500">{l}</div></div>))}
      </div>
      <div className="text-[11px] text-slate-500 font-mono">{c.subscription?.plan_name} · {c.subscription?.status}</div>
      <div className="flex gap-2 mt-auto">
        <Button size="sm" className="flex-1" onClick={onOpen} disabled={c.status !== "active"} data-testid={`agent-company-open-${c.id}`}>{current ? "Currently open" : "Open workspace"}<ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
        <Button size="sm" variant="secondary" onClick={onEdit} data-testid={`agent-company-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

export default function AgentCompanies() {
  const { me, reload } = useAuth();
  const [rows, setRows] = useState(null);
  const [ed, setEd] = useState(null);
  const load = () => api.get("/agent/companies").then((r) => setRows(r.data.companies)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!me?.agent) return <div className="text-slate-500" data-testid="agent-only-msg">This page is for Marketing Agent accounts.</div>;
  const open = (c) => {
    if (c.id === me.agent.home_organization_id) localStorage.removeItem("lx_org"); else localStorage.setItem("lx_org", c.id);
    window.location.assign("/dashboard");
  };
  return (
    <div data-testid="agent-companies-page" className="space-y-6">
      <PageHeader label="Marketing Agent" title="My Developer Companies" subtitle="Onboard developer companies and run their websites, marketing plans, creatives, campaigns, social media, leads and reports. Each company's data, projects and payments stay separate.">
        {me.user.role === "marketing_agent" && <Button onClick={() => setEd({ ...EMPTY })} data-testid="agent-add-company-button"><Plus className="h-4 w-4 mr-1" />Add company</Button>}
      </PageHeader>
      <AgentDiscountNote />
      {rows === null ? <div className="text-slate-500">Loading companies...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agent-companies-grid">
          {rows.map((c) => <CompanyCard key={c.id} c={c} current={c.id === me.organization?.id} onOpen={() => open(c)} onEdit={() => setEd({ ...EMPTY, ...c })} />)}
          {me.user.role === "marketing_agent" && rows.filter((c) => !c.is_agency).length === 0 && (
            <button onClick={() => setEd({ ...EMPTY })} className="lx-card border-dashed flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-emerald-600 min-h-[180px]" data-testid="agent-empty-add">
              <Building2 className="h-6 w-6" />Onboard your first developer company
            </button>)}
        </div>)}
      <CompanyDialog value={ed} onClose={() => setEd(null)} onSaved={() => { setEd(null); load(); reload(); }} />
    </div>
  );
}
